import React from 'react';

interface SectionWrapperProps {
  children: React.ReactNode;
  className?: string;
  background?: 'white' | 'light' | 'dark' | 'gradient';
  id?: string;
  containerSize?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  padding?: 'sm' | 'md' | 'lg';
}

const backgroundStyles = {
  white: 'bg-white',
  light: 'bg-neutral-50',
  dark: 'bg-neutral-900 text-white',
  gradient: 'bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white',
};

const containerStyles = {
  sm: 'max-w-3xl',
  md: 'max-w-5xl',
  lg: 'max-w-6xl',
  xl: 'max-w-7xl',
  full: 'max-w-none',
};

const paddingStyles = {
  sm: 'py-8 md:py-12',
  md: 'py-12 md:py-16',
  lg: 'py-16 md:py-20',
};

export function SectionWrapper({
  children,
  className = '',
  background = 'white',
  id,
  containerSize = 'lg',
  padding = 'md',
}: SectionWrapperProps) {
  return (
    <section
      id={id}
      className={`${backgroundStyles[background]} ${paddingStyles[padding]} ${className}`}
    >
      <div className={`mx-auto px-4 md:px-6 ${containerStyles[containerSize]}`}>{children}</div>
    </section>
  );
}

interface SectionHeaderProps {
  badge?: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
  className?: string;
}

export function SectionHeader({
  badge,
  title,
  description,
  align = 'center',
  className = '',
}: SectionHeaderProps) {
  const alignStyles = align === 'center' ? 'text-center' : 'text-left';

  return (
    <div className={`${alignStyles} mb-12 ${className}`}>
      {badge && (
        <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider bg-neutral-100 text-neutral-700 rounded-full mb-4">
          {badge}
        </span>
      )}
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-neutral-900 mb-4">
        {title}
      </h2>
      {description && (
        <p className="text-lg md:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
          {description}
        </p>
      )}
    </div>
  );
}
